import React, { Suspense, lazy, useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import Navbar from "../components/Navbar";
import StockTable from "../StockTable";
import DependencyMap from "../DependencyMap";
import "./Dashboard.css";
import "./DashboardOverlay.css";
import SupplyChain from "../SupplyChain";
import HiddenPairs from "../HiddenPairs";
import MarketOutlook from "../MarketOutlook";
import Feedback from "../Feedback";
import Strategies from "../Strategies";
import News from "../News";
import SmartMoney from "./SmartMoney";
import logo from "../assets/logo.png";
const PredictionMarkets = lazy(() => import("../PredictionMarkets"));

export default function Dashboard() {
  const [activeMenu, setActiveMenu] = useState("Stocks");

const [selectedSector, setSelectedSector] = useState(() => {
  try {
    const saved = sessionStorage.getItem(
      "bullionaire_stocks_dashboard_filters_v1"
    );

    if (!saved) return null;

    const parsed = JSON.parse(saved);
    return parsed?.selectedSector || null;
  } catch {
    return null;
  }
});

const [sidebarWidth, setSidebarWidth] = useState(220);
  const [collapsed, setCollapsed] = useState(false);
  const sidebarRef = useRef(null);
  const isResizing = useRef(false);
  const [sectorOpen, setSectorOpen] = useState(false);
  const [sectorPerformance, setSectorPerformance] = useState({});
  const [pendingStrategy, setPendingStrategy] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const shouldShowStartupOverlay =
    sessionStorage.getItem("showEmailConfirmedOverlay") === "true";

  const [showOverlay, setShowOverlay] = useState(shouldShowStartupOverlay);
  const [minimumTimeDone, setMinimumTimeDone] = useState(!shouldShowStartupOverlay);
  const [dashboardReady, setDashboardReady] = useState(false);
  const [fadeOutOverlay, setFadeOutOverlay] = useState(false);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };

  useEffect(() => {
  const dashboardPathMap = {
    "/dashboard": "Stocks",
    "/dashboard/market-outlook": "MarketOutlook",
    "/dashboard/dependency-map": "DependencyMap",
    "/dashboard/supply-chain": "SupplyChain",
    "/dashboard/hidden-pairs": "HiddenPairs",
    "/dashboard/watchlist": "Watchlist",
    "/dashboard/strategies": "Strategies",
    "/dashboard/smart-money": "SmartMoney",
    "/dashboard/news": "News",
    "/dashboard/feedback": "Feedback",
  };

  const clearSavedStockFilters = () => {
  try {
    sessionStorage.removeItem(
      "bullionaire_stocks_dashboard_filters_v1"
    );

    sessionStorage.removeItem(
      "bullionaire_stocks_dashboard_scroll_v1"
    );
  } catch {
    // Ignore storage errors
  }

  setSelectedSector(null);
};

  if (location.pathname.startsWith("/prediction-markets")) {
    clearSavedStockFilters();
    setActiveMenu("PredictionMarkets");
    setSectorOpen(false);
    return;
  }

  const menuFromPath = dashboardPathMap[location.pathname];

  if (menuFromPath) {
    setActiveMenu(menuFromPath);
    setSectorOpen(false);

    if (menuFromPath !== "Stocks") {
      clearSavedStockFilters();
    }
  }
}, [location.pathname]);

  const API_BASE =
    process.env.NODE_ENV === "development"
      ? "http://localhost:8000"
      : process.env.REACT_APP_API_BASE;

  const sectorList = [
    "Basic Materials",
    "Communication Services",
    "Consumer Cyclical",
    "Consumer Defensive",
    "Energy",
    "Financial Services",
    "Healthcare",
    "Industrials",
    "Real Estate",
    "Technology",
    "Utilities",
  ];

  useEffect(() => {
    const fetchSectorPerformance = async () => {
      try {
        const res = await fetch(`${API_BASE}/sector-performance`);
        const data = await res.json();

        if (data && typeof data === "object") {
          setSectorPerformance(data);
        } else {
          setSectorPerformance({});
        }
      } catch (err) {
        console.error("Failed to load sector performance", err);
        setSectorPerformance({});
      } finally {
        setDashboardReady(true);
      }
    };

    fetchSectorPerformance();
  }, [API_BASE]);

  useEffect(() => {
    if (!shouldShowStartupOverlay) return;

    const timer = setTimeout(() => {
      setMinimumTimeDone(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, [shouldShowStartupOverlay]);

  useEffect(() => {
  if (!showOverlay) return;
  if (!minimumTimeDone) return;
  if (!dashboardReady) return;

  setFadeOutOverlay(true);

  const fadeTimer = setTimeout(() => {
    setShowOverlay(false);
    setFadeOutOverlay(false);
    sessionStorage.removeItem("showEmailConfirmedOverlay");
  }, 400);

  return () => clearTimeout(fadeTimer);
}, [showOverlay, minimumTimeDone, dashboardReady]);

  const startResizing = () => {
    isResizing.current = true;
    document.addEventListener("mousemove", resizeSidebar);
    document.addEventListener("mouseup", stopResizing);
  };

  const resizeSidebar = (e) => {
    if (!isResizing.current) return;
    let newWidth = e.clientX;
    if (newWidth < 60) newWidth = 60;
    if (newWidth > 400) newWidth = 400;
    setSidebarWidth(newWidth);
  };

  const stopResizing = () => {
    isResizing.current = false;
    document.removeEventListener("mousemove", resizeSidebar);
    document.removeEventListener("mouseup", stopResizing);
  };
const openStrategyFromIdea = (strategyIdea) => {
    if (!strategyIdea || !Array.isArray(strategyIdea.positions)) return;

    const cleanPositions = strategyIdea.positions
      .filter((position) => position.ticker && Number(position.weight) !== 0)
      .map((position) => ({
        ticker: String(position.ticker).trim().toUpperCase(),
        weight: Number(position.weight),
      }));

    if (!cleanPositions.length) return;

    setPendingStrategy({
      id: `${Date.now()}-${strategyIdea.name || "strategy"}`,
      name: strategyIdea.name || "Imported Strategy",
      mode: strategyIdea.mode || "strategy",
      source: strategyIdea.source || "Bullionaire",
      notes: strategyIdea.notes || "",
      positions: cleanPositions,
    });

    setActiveMenu("Strategies");
    setSectorOpen(false);
    navigate("/dashboard/strategies");
  };
  return (
    <>
      <Navbar />

      <div className="dashboard">
        <aside
          className={`sidebar ${collapsed ? "collapsed" : ""}`}
          style={{ width: collapsed ? 60 : sidebarWidth }}
          ref={sidebarRef}
        >
          <div className="sidebar-top">
            <h2 className="sidebar-title">{collapsed ? "M" : "Market Lens"}</h2>
            <button
              className="collapse-btn"
              onClick={() => setCollapsed(!collapsed)}
            >
              {collapsed ? "➡" : "⬅"}
            </button>
          </div>

          <nav className="sidebar-nav">
            <div className="sidebar-main-container">
              <div
                className={`sidebar-main ${activeMenu === "Stocks" ? "active" : ""}`}
                onClick={() => {
                  navigate("/dashboard");
                  setActiveMenu("Stocks");
                  setSelectedSector(null);
                  setSectorOpen(false);
                }}
                style={{
                  backgroundColor: activeMenu === "Stocks" ? "#19C37D" : "transparent",
                  cursor: "pointer",
                  padding: "8px 12px",
                  borderRadius: "4px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <span
                  style={{
                    color: activeMenu === "Stocks" ? "#001f3f" : "#fff",
                    fontWeight: "bold",
                  }}
                >
                  Stocks
                </span>

                {!collapsed && (
                  <span
                    className="dropdown-arrow"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSectorOpen(!sectorOpen);
                    }}
                    style={{
                      cursor: "pointer",
                      fontSize: "30px",
                    }}
                  >
                    {sectorOpen ? "▾" : "▸"}
                  </span>
                )}
              </div>
            </div>

            {sectorOpen && !collapsed && (
              <div className="sector-list">
                {sectorList.map((sector) => {
                  const change = sectorPerformance[sector];
                  const hasValue = typeof change === "number" && !Number.isNaN(change);

                  return (
                    <div
                      key={sector}
                      className="sector-item"
                      onClick={() => {
                        navigate("/dashboard");
                        setActiveMenu("Stocks");
                        setSelectedSector(sector);
                      }}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <span>{sector}</span>

                      <span
                        style={{
                          color: !hasValue
                            ? "#9ca3af"
                            : change > 0
                            ? "#16a34a"
                            : change < 0
                            ? "#dc2626"
                            : "#ffffff",
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {hasValue ? `${change > 0 ? "+" : ""}${change.toFixed(2)}%` : "--"}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

<div
  className={`sidebar-item ${activeMenu === "PredictionMarkets" ? "active" : ""}`}
  onClick={() => {
    navigate("/prediction-markets");
    setActiveMenu("PredictionMarkets");
    setSectorOpen(false);
  }}
>
  Prediction Markets
</div>

            <div
              className={`sidebar-item ${activeMenu === "MarketOutlook" ? "active" : ""}`}
              onClick={() => {
              navigate("/dashboard/market-outlook");
              setActiveMenu("MarketOutlook");
              setSectorOpen(false);
            }}
            >
              Market Outlook
            </div>

            <div
              className={`sidebar-item ${activeMenu === "DependencyMap" ? "active" : ""}`}
              onClick={() => {
              navigate("/dashboard/dependency-map");
              setActiveMenu("DependencyMap");
              setSectorOpen(false);
            }}
            >
              Dependency Map
            </div>

            <div
              className={`sidebar-item ${activeMenu === "SupplyChain" ? "active" : ""}`}
              onClick={() => {
            navigate("/dashboard/supply-chain");
            setActiveMenu("SupplyChain");
            setSectorOpen(false);
            }}
            >
              Supply Chain
            </div>

            <div
              className={`sidebar-item ${activeMenu === "HiddenPairs" ? "active" : ""}`}
              onClick={() => {
            navigate("/dashboard/hidden-pairs");
            setActiveMenu("HiddenPairs");
            setSectorOpen(false);
            }}
            >
              Hidden Pairs
            </div>

            <div
  className={`sidebar-item ${activeMenu === "Watchlist" ? "active" : ""}`}
  onClick={() => {
  navigate("/dashboard/watchlist");
  setActiveMenu("Watchlist");
  setSectorOpen(false);
}}
>
  Watchlist
</div>

<div
  className={`sidebar-item ${activeMenu === "Strategies" ? "active" : ""}`}
  onClick={() => {
  navigate("/dashboard/strategies");
  setActiveMenu("Strategies");
  setSectorOpen(false);
}}
>
  Strategies
</div>

<div
  className={`sidebar-item ${activeMenu === "SmartMoney" ? "active" : ""}`}
  onClick={() => {
  navigate("/dashboard/smart-money");
  setActiveMenu("SmartMoney");
  setSectorOpen(false);
}}
>
  Smart Money
</div>

<div
  className={`sidebar-item ${activeMenu === "News" ? "active" : ""}`}
  onClick={() => {
  navigate("/dashboard/news");
  setActiveMenu("News");
  setSectorOpen(false);
}}
>
  News
</div>

<div
  className={`sidebar-item ${activeMenu === "Feedback" ? "active" : ""}`}
  onClick={() => {
  navigate("/dashboard/feedback");
  setActiveMenu("Feedback");
  setSectorOpen(false);
}}
>
  Feedback
</div>
          </nav>

          {!collapsed && <div className="resizer" onMouseDown={startResizing} />}
        </aside>

        <main
  className="main-content"
  style={{
    backgroundColor: "#0E1424",
    padding: "40px",
    position: "relative",
  }}
>
  {activeMenu === "PredictionMarkets" ? (
    <Suspense fallback={<div>Loading prediction markets…</div>}>
      <PredictionMarkets />
    </Suspense>
  ) : activeMenu === "DependencyMap" ? (
    <DependencyMap onBuildStrategy={openStrategyFromIdea} />
  ) : activeMenu === "SupplyChain" ? (
    <SupplyChain onBuildStrategy={openStrategyFromIdea} />
  ) : activeMenu === "HiddenPairs" ? (
    <HiddenPairs onBuildStrategy={openStrategyFromIdea} />
  ) : activeMenu === "MarketOutlook" ? (
    <MarketOutlook />
  ) : activeMenu === "Strategies" ? (
    <Strategies importedStrategy={pendingStrategy} />
  ) : activeMenu === "SmartMoney" ? (
    <SmartMoney />
  ) : activeMenu === "News" ? (
    <News />
  ) : activeMenu === "Feedback" ? (
    <Feedback />
  ) : (
    <StockTable
  view={activeMenu}
  selectedSector={selectedSector}
  setSelectedSector={setSelectedSector}
/>
  )}
</main>
      </div>

      {showOverlay && (
        <div className={`dashboard-loading-overlay ${fadeOutOverlay ? "fade-out" : ""}`}>
          <div className="dashboard-loading-card">
            <h1>Email Confirmed</h1>
            <p>Getting your dashboard ready...</p>

            <div className="logo-ring-wrap">
              <img src={logo} alt="Bullionaire logo" className="overlay-logo" />

              <svg className="progress-ring" viewBox="0 0 120 120">
                <circle className="progress-ring-bg" cx="60" cy="60" r="54" />
                <circle className="progress-ring-fill" cx="60" cy="60" r="54" />
              </svg>
            </div>
          </div>
        </div>
      )}
    </>
  );
}